import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ClipboardList } from "lucide-react";

import { LegalFooter } from "../chalk/LegalLayout";
import ChalkDust from "../chalk/ChalkDust";
import { BODY, C, DISPLAY, MARKER, SectionHead, TopBar, sectionBase } from "../../pages/Landing";
import { EARLY_ACCESS_NOTE, usePaymentsOpen } from "../../lib/billingStatus";
import { AudienceCards, FeatureGrid, ShotSteps } from "./Blocks";

export default function AudiencePage({ id, docTitle, eyebrow, title, marker, intro, steps, featureTitle, featureSub, features, cta = "Get Chalk OS", ctaTo = "/login?mode=signup", ctaHref, extra }) {
  const open = usePaymentsOpen();
  useEffect(() => {
    const prev = document.title;
    document.title = docTitle;
    return () => { document.title = prev; };
  }, [docTitle]);
  const Cta = ({ testId, light }) => ctaHref ? (
    <a href={ctaHref} data-testid={testId} className={light ? "chalk-btn-athletic" : "chalk-btn-athletic chalk-btn-athletic--dark"}>{cta} <ArrowRight size={14} aria-hidden="true" /></a>
  ) : (
    <Link to={ctaTo} data-testid={testId} className={light ? "chalk-btn-athletic" : "chalk-btn-athletic chalk-btn-athletic--dark"}>
      <ClipboardList size={16} aria-hidden="true" /> {cta}
    </Link>
  );
  return (
    <div data-testid={`${id}-page`} style={{ fontFamily: BODY, color: C.textDark, background: C.cream }}>
      <a href="#main" className="chalk-skip-link">Skip to main content</a>
      <TopBar />
      <main id="main" tabIndex={-1}>
        <section className="chalkboard-texture" style={{ background: C.greenDark, color: C.chalkWhite, padding: "80px 22px 88px", position: "relative", overflow: "hidden" }}>
          <ChalkDust count={14} opacity={0.4} />
          <div className="chalk-fade-up" style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 2 }}>
            <span className="chalk-marker-eyebrow" data-testid={`${id}-eyebrow`}>{eyebrow}</span>
            <h1 data-testid={`${id}-title`} style={{ fontFamily: DISPLAY, fontWeight: 400, fontSize: "clamp(3rem, 7vw, 5.6rem)", lineHeight: 0.92, textTransform: "uppercase", margin: "22px 0 6px", maxWidth: 860 }}>{title}</h1>
            <div style={{ fontFamily: MARKER, fontSize: "clamp(2rem, 4.5vw, 3.4rem)", color: C.greenChalk, transform: "rotate(-2deg)", transformOrigin: "left", display: "inline-block", margin: "6px 0 24px" }}>{marker}</div>
            <p style={{ fontSize: 17, lineHeight: 1.6, color: C.chalkMuted, maxWidth: 600, margin: "0 0 30px" }}>{intro}</p>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <Cta testId={`${id}-hero-cta`} light />
              <a href="#how-it-works" data-testid={`${id}-how-link`} className="chalk-btn-athletic chalk-btn-athletic--outline">See how it works</a>
            </div>
          </div>
        </section>

        <section id="how-it-works" style={{ ...sectionBase, background: C.cream }}>
          <div style={{ maxWidth: 1100, margin: "0 auto" }}>
            <SectionHead title="How it works." subtitle="Real screens from the live app." align="left" />
            <ShotSteps steps={steps} idPrefix={id} />
          </div>
        </section>

        <section style={{ ...sectionBase, background: C.offWhite }}>
          <div style={{ maxWidth: 1100, margin: "0 auto" }}>
            <SectionHead title={featureTitle} subtitle={featureSub} align="left" />
            <FeatureGrid keys={features} idPrefix={id} />
            {!open && <p data-testid={`${id}-early-access`} style={{ fontSize: 13, color: C.muted, marginTop: 22 }}>{EARLY_ACCESS_NOTE} <Link to="/pricing" style={{ color: C.greenMid, fontWeight: 700 }}>See plans</Link></p>}
            {extra}
          </div>
        </section>

        <section style={{ ...sectionBase, background: C.cream }}>
          <div style={{ maxWidth: 1100, margin: "0 auto" }}>
            <SectionHead title="Who else is on the field." align="left" />
            <AudienceCards />
          </div>
        </section>

        <section className="chalkboard-texture" style={{ background: C.greenDark, padding: "80px 22px", textAlign: "center" }}>
          <SectionHead title="Start free." subtitle="Upgrade when you're ready." dark />
          <div style={{ marginTop: 28 }}><Cta testId={`${id}-final-cta`} light /></div>
        </section>
      </main>
      <LegalFooter />
    </div>
  );
}
